import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback } from "react";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from "react-google-recaptcha-v3";
import {
  useForm,
  Controller,
  type SubmitHandler,
  FormProvider,
} from "react-hook-form";
import { z } from "zod";

import type { LoginInput } from "../../api/login";
import { loginSchemaInput, login } from "../../api/login";
import { verifyRecaptcha } from "../../api/recaptcha";
import DocumentTextField from "../../components/DocumentTextField";
import HiddenTextField from "../../components/HidenTextField";
import isAuthenticated from "../../utils/isAuthenticated";
import { removeNotNumbers } from "../../utils/removeNotNumbers";
import { notify, notifyWithTitle, ToastNotifyStyle } from "../../pages/public/components/Toast";
import axios, { AxiosError } from "axios";
import extractInnerMessage from "../../utils/apiErrorUtils";

const fallback = "/" as const;

export const Route = createFileRoute("/__public/login")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: ({ search }) => {
    if (isAuthenticated()) {
      throw redirect({ to: search.redirect || fallback });
    }
  },
  component: Login,
});

function Login(): React.JSX.Element {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const from = search.redirect || "/";

  const methods = useForm<LoginInput>({
    defaultValues: { document: "", password: "" },
    resolver: yupResolver(loginSchemaInput),
    criteriaMode: "all",
    mode: "all",
  });

  const { formState, control, handleSubmit, setError } = methods;

  const mutation = useMutation({
    mutationFn: async (input: LoginInput) => {
      const payload = {
        document: removeNotNumbers(String(input.document)),
        password: input.password,
      };
      return login(payload);
    },
    onSuccess: (response) => {
      localStorage.setItem("token", response.token);
      localStorage.setItem("user", JSON.stringify(response.user));
      void navigate({ to: from });
    },
    onError: (error) => {
      const apiErrorMessage = extractInnerMessage(error);
      console.error("login error:", apiErrorMessage);

      if(axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if(axiosError.response?.status === 400) {
          const data = axiosError.response.data as { message?: string; code?: string };

          notify(data.message || t("login.loginError"), "error", data.code || "");
        } else{
          notifyWithTitle(t("login.loginErrorTitle"), apiErrorMessage, "error", { fontSize: "16px" } as ToastNotifyStyle);
        }
      } else {
        notifyWithTitle(t("login.loginErrorTitle"), apiErrorMessage, "error", { fontSize: "16px" } as ToastNotifyStyle);
      }
    }
  });

  const onVerify = useCallback(
    async (token: string) => {
      const recaptchaResponse = await verifyRecaptcha(token);
      if (!recaptchaResponse) {
        setError("root.recaptcha", {
          message:
            t("login.recaptchaError"),
        });
      }
    },
    [setError],
  );

  const onSubmit: SubmitHandler<LoginInput> = (data): void => {
    mutation.mutate(data);
  };

  const hasRecaptchaKey = Boolean(
    window.config?.reSiteKey &&
      window.config.reSiteKey !== "PORTAL_EXTERNO_PLACEHOLDER_RE_SITE_KEY"
  );
  const recaptchaKey: string = hasRecaptchaKey ? String(window.config.reSiteKey) : "";

  return (
    <GoogleReCaptchaProvider
      language="pt-BR"
      reCaptchaKey={recaptchaKey}
    >
      <FormProvider {...methods}>
        <Stack height="100%" justifyContent="space-between">
          <div>
            <form
              autoComplete="off"
              autoSave="off"
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            >
              <Stack alignItems="center" gap={3}>
                <Typography
                  fontSize="28px"
                  fontWeight="500"
                  marginRight="auto"
                  variant="h4"
                >
                  {window.constants?.titleLogin ||
                    t("login.title")}
                </Typography>
                <input name="redirectTo" type="hidden" value={from} />
                <Box sx={{ width: "100%" }}>
                  <Controller
                    control={control}
                    name="document"
                    render={({ field, fieldState }) => {
                      const { error } = fieldState;
                      return (
                        <DocumentTextField
                          {...field}
                          error={Boolean(error)}
                          fullWidth
                          helperText={error ? error.message : " "}
                          name="document"
                          variant="outlined"
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name="password"
                    render={({ field, fieldState }) => {
                      const { error } = fieldState;
                      return (
                        <HiddenTextField
                          {...field}
                          error={Boolean(error)}
                          fullWidth
                          helperText={error ? error.message : " "}
                          label={t("login.passwordLabel")}
                          name="password"
                          variant="outlined"
                        />
                      );
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        name="keepDataOnLogin"
                        size="small"
                        sx={{ "& .MuiSvgIcon-root": { color: "primary.main" } }}
                      />
                    }
                    label={t("login.saveCredentials")}
                    labelPlacement="end"
                    value
                  />
                </Box>
                {formState.errors.root?.recaptcha.message ? (
                  <Alert severity="error">
                    {formState.errors.root.recaptcha.message}
                  </Alert>
                ) : null}
                {hasRecaptchaKey ? (
                  <GoogleReCaptcha onVerify={onVerify} />
                ) : null}
                <Stack gap={3} style={{ width: "100%", marginTop: "2rem" }}>
                  <Stack gap={2}>
                    <Button
                      color="primary"
                      disabled={
                        Boolean(formState.errors.root?.recaptcha.message) &&
                        !formState.isValid
                      }
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {mutation.isPending ? t("login.submitting") : t("login.submit")}
                    </Button>
                    <Button
                      color="secondary"
                      component={Link}
                      fullWidth
                      size="large"
                      to="/forgot-password"
                      variant="text"
                    >
                      {t("login.forgotPassword")}
                    </Button>
                  </Stack>
                  <Button
                    color="secondary"
                    component={Link}
                    fullWidth
                    size="medium"
                    to="/register"
                    variant="outlined"
                  >
                    {t("login.noAccount")}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </div>
          <Stack alignItems="center" component="footer" gap={0.5}>
            <Typography color="textSecondary" variant="caption">
              {t("common.smartdocsPortal")}
            </Typography>
            {typeof window.versionSystem === "string" &&
            window.versionSystem.trim() ? (
              <Typography color="textSecondary" variant="caption">
                {window.versionSystem}
              </Typography>
            ) : (
              t("common.noVersionInfo")
            )}
          </Stack>
        </Stack>
      </FormProvider>
    </GoogleReCaptchaProvider>
  );
}
